import i18n from "../../i18n";

/**
 * Shared policy for API snackbars: show server `message` when present; otherwise a translated fallback
 * so empty or English-only responses still get Urdu copy when the UI is in Urdu.
 *
 * Use for `enqueueSnackbar(...)` after API calls.
 */

function normalize(input) {
  return String(input || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function translate(t, key) {
  if (typeof t === "function") return t(key);
  if (typeof i18n?.t === "function") return i18n.t(key);
  return key;
}

function isUrduUi(t) {
  const lang =
    t?.i18n?.resolvedLanguage ||
    t?.i18n?.language ||
    i18n?.resolvedLanguage ||
    i18n?.language ||
    "";
  return normalize(lang).startsWith("ur");
}

function translateEntityUr(entity) {
  const map = {
    feed: "پوسٹ",
    message: "پیغام",
    comment: "تبصرہ",
    answer: "جواب",
    activity: "سرگرمی",
    class: "کلاس",
    subject: "مضمون",
    note: "نوٹ",
    "fee request": "فیس درخواست",
    "fee requests": "فیس درخواستیں",
    "personal note": "ذاتی نوٹ",
    "gallery folder": "گیلری فولڈر",
    "gallery book": "گیلری بک",
    "teacher class access": "استاد کی کلاس رسائی",
    "teacher subject access": "استاد کی مضمون رسائی",
    "student class access": "طالب علم کی کلاس رسائی",
    "student subject access": "طالب علم کی مضمون رسائی",
  };
  return map[normalize(entity)] || entity;
}

function localizeKnownMessage(message, t, fallbackKey) {
  const msg = String(message || "").trim();
  if (!msg) return translate(t, fallbackKey);
  if (!isUrduUi(t)) return msg;

  const added = msg.match(/^(.+)\s+created successfully$/i);
  if (added) {
    return `${translateEntityUr(added[1])} ${translate(t, "success.saved")}`;
  }

  const updated = msg.match(/^(.+)\s+updated successfully$/i);
  if (updated) {
    return `${translateEntityUr(updated[1])} ${translate(t, "success.updated")}`;
  }

  const deleted = msg.match(/^(.+)\s+deleted successfully$/i);
  if (deleted) {
    return `${translateEntityUr(deleted[1])} ${translate(t, "success.deleted")}`;
  }

  return msg;
}

/**
 * @param {object} result
 * @param {import("i18next").TFunction} t
 * @param {string} [fallbackKey="errors.requestFailed"]
 * @returns {string}
 */
export function apiErrorText(result, t, fallbackKey = "errors.requestFailed") {
  const msg = result?.message;
  return localizeKnownMessage(msg, t, fallbackKey);
}

/**
 * @param {object} result
 * @param {import("i18next").TFunction} t
 * @param {string} [fallbackKey="success.generic"]
 * @returns {string}
 */
export function apiSuccessText(result, t, fallbackKey = "success.generic") {
  const msg = result?.message;
  return localizeKnownMessage(msg, t, fallbackKey);
}

/**
 * Same pattern for socket events and other payloads with a `message` field.
 * @param {object} payload
 * @param {import("i18next").TFunction} t
 * @param {string} [fallbackKey="errors.requestFailed"]
 */
export function payloadMessage(payload, t, fallbackKey = "errors.requestFailed") {
  const msg = payload?.message;
  return localizeKnownMessage(msg, t, fallbackKey);
}
